import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useNavigate, useLocation } from 'react-router-dom';
import { OutletContext } from '../../layout/Layout';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { Card, CardBody, Grid, GridItem } from '@patternfly/react-core';
import './Discover.scss';
import { DiscoverCategory, DiscoverContext, QuickFilter } from '../../types/discover';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { SubNavContext } from '../../helpers/sub-nav-contexts/sub-nav-context';
import { TQuestion } from '../../api/questions/Questions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import ChatInterface from '../../components/data-builder/ChatInterface';
import { SavedChat, SavedChats } from './SavedChats';
import { ChatActions } from '../../api/lambda/ChatActions';
import { ChatSession } from '../../api/types';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { useApplication } from '../../components/user/ApplicationProvider';

/**
 * ZAP-2578: the session list hands us a calendar date as YYYY-MM-DD. Build the
 * Date from its parts — `new Date('YYYY-MM-DD')` parses as UTC midnight and
 * shows the previous day anywhere west of Greenwich.
 */
const formatIsoDate = (iso?: string): string | undefined => {
	if (!iso) return undefined;
	const [y, m, d] = iso.split('-').map(Number);
	if (!y || !m || !d) return iso;
	return new Date(y, m - 1, d).toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

type Props = {
	pageContext: DiscoverContext;
};
const AdvancedAI = (props: Props) => {
	const navigate = useNavigate();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [categories, setCategories] = useState<DiscoverCategory[]>();
	const [hasLoadedCategories, setHasLoadedCategories] = useState<boolean>(false);
	const [quickFilters, setQuickFilters] = useState<QuickFilter[]>();
	const [filteredQuickFilters, setFilteredQuickFilters] = useState<QuickFilter[]>();
	const [_, setSelectedDate] = useState<TNewDateRange>();
	const [subNavContext, setSubNavContext] = useState<SubNavContext>();
	const [allQuestions, setAllQuestions] = useState<TQuestion[]>();
	const [aiSearchEnabled, setAiSearchEnabled] = useState(false);
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const termParam = params.get('term') ?? undefined;
	// initialTerm will be passed into the chat as the auto-submit message (if present)
	const initialTerm = termParam;
	const [selectedSession, setSelectedSession] = useState<string | undefined>(
		new Date().getTime().toString()
	);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>();
	const { user } = useApplication();
	const [loading, setLoading] = useState(false);
	const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
	const [currentChatDate, setCurrentChatDate] = useState<string>();
	const lastMessageDate = formatIsoDate(currentChatDate);
	const panelRef = useRef<HTMLDivElement>(null);
	const [panelWidth, setPanelWidth] = useState<number>(0);

	// ZAP-2462: warm the AI Analyst path (token prefetch + TLS/health ping) on mount
	// so the exchange is off the first-message critical path. Fire-and-forget.
	useEffect(() => {
		ChatActions.warmup();
	}, []);

	useEffect(() => {
		const measure = () => {
			if (panelRef.current) {
				setPanelWidth(panelRef.current.clientWidth);
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		if (panelRef.current) observer.observe(panelRef.current);
		return () => observer.disconnect();
	}, []);

	const handleSelectChat = (sessionId: string, jobId: string) => {
		setSelectedSession(sessionId);
		setSelectedJobId(jobId);
		setCurrentChatDate(savedChats.find((x) => x.session_id == sessionId)?.last_activity ?? '');
	};

	const handleNewChat = () => {
		setSelectedSession(new Date().getTime().toString());
		setSelectedJobId(undefined);
		setCurrentChatDate(undefined);
		void fetchSavedChats();
	};

	// ZAP-2580: the sessions list is only fetched when the sidebar mounts and
	// when "New Chat" is clicked, so a conversation started here stayed out of
	// Recents until the page was reloaded. ChatInterface reports every saved
	// exchange through onSaveChat; list the session the first time we hear
	// about it. The server orders sessions newest-first, so it goes on top.
	const handleChatSaved = (sessionId: string, jobId: string, title: string) => {
		setSavedChats((prev) => {
			// onSaveChat fires once per assistant reply and again when a chat
			// is opened from Recents, so anything but an insert-if-absent would
			// either duplicate the entry or backdate an existing one.
			if (prev.some((chat) => chat.session_id === sessionId)) return prev;

			const today = new Date().toISOString().split('T')[0];

			return [
				{
					session_id: sessionId,
					// contextId is undefined for a chat started here and
					// reaches us as the string "undefined"; session_id is what
					// fetchSavedChats falls back to and what the history
					// endpoint actually keys on.
					job_id: jobId && jobId !== 'undefined' ? jobId : sessionId,
					title,
					created_at: today,
					last_activity: today,
					numberOfMsgs: 2,
				},
				...prev,
			];
		});
	};

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle=""
					pageDescription=""
					subNavContext={undefined}
				/>
			),
			sidebarContext: null,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	// update the search handler (URL-escape terms)
	const handleSearch = (_event: any, value: string) => {
		const encoded = encodeURIComponent(value || '');
		if (aiSearchEnabled) {
			navigate(`/discover/analyst?term=${encoded}`);
		} else {
			navigate(`/discover/search?term=${encoded}`);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && searchTerm && searchTerm.trim().length > 0) {
			const encoded = encodeURIComponent(searchTerm);
			if (aiSearchEnabled) {
				navigate(`/discover/analyst?term=${encoded}`);
			} else {
				navigate(`/discover/search?term=${encoded}`);
			}
		}
	};

	const fetchSavedChats = async () => {
		setLoading(true);
		try {
			const userIdentifer = user.account + getEnvironmentCode();
			const response = await ChatActions.getInovationAIChatSessions(25, userIdentifer);

			if (response.sessions && response.sessions.length > 0) {
				// Convert ChatSession to SavedChat format
				const convertedChats: SavedChat[] = response.sessions.map(
					(session: ChatSession) => ({
						session_id: session.session_id,
						job_id: session.job_id ?? session.session_id,
						title: session.title ?? '',
						created_at: new Date(session.created_at!).toISOString().split('T')[0],
						numberOfMsgs: session.message_count ?? 1,
						last_activity: new Date(session.last_activity!).toISOString().split('T')[0],
					})
				);

				setSavedChats(convertedChats);
			}
		} catch (error) {
			console.error('Error fetching saved chats:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid span={12}>
			<GridItem span={2}>
				{/* Left sidebar with saved chats */}
				<SavedChats
					onSelectChat={handleSelectChat}
					onNewChat={handleNewChat}
					fetchSavedChats={fetchSavedChats}
					isLoading={loading}
					setLoading={setLoading}
					setSavedChats={setSavedChats}
					savedChats={savedChats}
				/>
			</GridItem>
			<GridItem span={10}>
				<Card className="discover-container advanced-ai-panel">
					<CardBody
						className="advanced-ai-body"
						ref={panelRef}
					>
						<div className="advanced-ai-header">
							<h1>
								<FontAwesomeIcon
									icon={faSparkles}
									style={{ paddingRight: '5px' }}
								/>
								<span className="insights-sticky-header__title"> AI Analyst</span>
							</h1>
							{/* ZAP-2578: the old "Last Activity: <date>" chip never said what the
							    date was; say it, quietly, under the title. */}
							{lastMessageDate && (
								<p className="advanced-ai-chat-meta">
									Last message on {lastMessageDate}
								</p>
							)}
						</div>
						{/* Main chat area */}
						<ChatInterface
							isSinglePage={true}
							externalThread={false}
							autoSubmitMessage={initialTerm}
							useInnovationAI={true}
							canSaveChat={true}
							onSaveChat={handleChatSaved}
							sessionId={selectedSession}
							contextId={selectedJobId}
							onAutoSubmitted={() => navigate('/discover/analyst', { replace: true })}
							maxPanelWidth={panelWidth}
						/>
					</CardBody>
				</Card>
			</GridItem>
		</Grid>
	);
};

export default AdvancedAI;
